'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { ActiveFiltersType, Filter, PageHeader, ProductList } from '@bytel/product-wall';
import {
    addToActiveFilters,
    classNames,
    countActiveFiltersClearable,
    resetClearableActiveFilters,
} from '@bytel/product-wall/helpers';
import { FilterType, NamePosition } from '@bytel/product-wall/types';
import {
    Align,
    Button,
    ButtonVariant,
    Container,
    FlexBox,
    FlexItem,
    IconName,
    Input,
    InputType,
    Justify,
    PriceLevel,
    Section,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { odrService } from '@services/odr';
import { wallService } from '@services/products/wall';

import { WallConfigType } from '@app-types/config';
import { Odr } from '@app-types/odr';
import { SearchParamsType } from '@app-types/page';
import { OdrProduct } from '@app-types/product';

import { ImageProps } from '@components/image-thumbor';
import { LinkCustom } from '@components/link-custom';
import { EmptyProductList } from '@components/walls';

import { replaceWindowUrl, setDocumentTitle } from '@helpers/document';
import {
    addSearchParams,
    generateHref,
    generateTitle,
    getActiveFilters,
    removeSearchParams,
    resetSearchParams,
} from '@helpers/seo';
import { sendFilterTracking, sendResetFilterTracking } from '@helpers/walls';

import { odrSkeleton } from '@constants/skeletons';

import { Breadcrumb } from '@odr-wall/components';
import {
    sendOdrDateCalendarOpenTracking,
    sendOdrDateSelectionTracking,
    sendOdrSearchTracking,
    sendOdrWallPageTracking,
} from '@odr-wall/helpers';
import { format, parse } from 'date-fns';

import { OdrDetailModal } from './odr-detail-modal';

export function OdrImage({ className = '', ...props }: Readonly<ImageProps>) {
    // eslint-disable-next-line jsx-a11y/alt-text
    return <img className={classNames(className, 'odr-image')} {...props} />;
}

interface OdrWallProps {
    title: string;
    products: OdrProduct[];
    activeFilters?: ActiveFiltersType;
    pageSize?: number;
    wallConfig: WallConfigType;
    filters: FilterType[];
    searchParams?: SearchParamsType;
    search?: string;
    date?: string;
}

export function OdrWall({
    title,
    wallConfig,
    filters,
    products,
    search,
    date,
    pageSize = 30,
    searchParams: _searchParams,
    activeFilters: _activeFilters = {},
}: Readonly<OdrWallProps>) {
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFilters, setCurrentFilters] = useState(_activeFilters);

    const [isLoading, setIsLoading] = useState(false);
    const [productsList, setProductsList] = useState(products);
    const [searchParams, setSearchParams] = useState<URLSearchParams>(
        _searchParams ? new URLSearchParams(_searchParams) : new URLSearchParams(),
    );
    const [selectedOdr, setSelectedOdr] = useState<Odr | undefined>(undefined);
    const [hasToScroll, setHasToScroll] = useState(0);
    const [currentSearch, setCurrentSearch] = useState(search);

    const [currentDate, setCurrentDate] = useState(
        date ? format(parse(date, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd') : '',
    );

    const loadOdrDetails = (productId: string) => {
        odrService.getOdrById(productId).then(setSelectedOdr);
    };

    const handleCloseModal = () => {
        setSelectedOdr(undefined);
    };

    const filteredProducts = useMemo(() => {
        let filtered = productsList;
        if (currentSearch) {
            filtered = filtered.filter((odr) => odr.name.toLowerCase().includes(currentSearch.toLowerCase()));
        }

        if (currentDate) {
            const filterDate = parse(currentDate, 'yyyy-MM-dd', new Date());
            filtered = filtered.filter((odr) => {
                const odrEndDate = parse(odr.endDate, 'dd/MM/yyyy', new Date());
                const odrStartDate = parse(odr.startDate, 'dd/MM/yyyy', new Date());
                return filterDate >= odrStartDate && filterDate <= odrEndDate;
            });
        }

        return filtered;
    }, [productsList, currentSearch, currentDate]);

    const count = filteredProducts.length;

    const href = (name: string, value: string, active = currentFilters, urlSearch = searchParams): string => {
        if (name === '') {
            return wallConfig.baseUrl;
        }
        return `${wallConfig.baseUrl}/${generateHref(
            wallConfig,
            addToActiveFilters(active, name, value),
            urlSearch,
            false,
        )}`;
    };

    const onFilter = async (activeFilters: ActiveFiltersType, urlSearchParams?: URLSearchParams) => {
        setCurrentFilters(activeFilters);
        setIsLoading(true);
        await updateProducts(generateHref(wallConfig, activeFilters, urlSearchParams ?? searchParams, false));
    };

    const onFilterTracking = (label: string, value: string) => {
        const hasBeenUnchecked = currentFilters[label]?.includes(value);

        sendFilterTracking(wallConfig, false, label, value, !hasBeenUnchecked);
    };

    const onSearch = async (value: string) => {
        setCurrentSearch(value);

        let newSearchParams;
        if (value) {
            sendOdrSearchTracking();
            newSearchParams = addSearchParams('nom', value, searchParams);
        } else {
            newSearchParams = removeSearchParams('nom', searchParams);
        }
        setSearchParams(newSearchParams);

        const queryString = newSearchParams.toString();
        const url = queryString ? `${wallConfig.baseUrl}?${queryString}` : wallConfig.baseUrl;
        replaceWindowUrl(url);
    };

    const onDateChange = async (value: string) => {
        setCurrentDate(value);

        if (!value || /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            let newSearchParams;
            if (value) {
                sendOdrDateSelectionTracking();
                const formattedDate = format(parse(value, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy');
                newSearchParams = addSearchParams('date', formattedDate, searchParams);
            } else {
                newSearchParams = removeSearchParams('date', searchParams);
            }
            setSearchParams(newSearchParams);

            const queryString = newSearchParams.toString();
            const url = queryString ? `${wallConfig.baseUrl}?${queryString}` : wallConfig.baseUrl;
            replaceWindowUrl(url);
        }
    };

    const onDateClick = () => {
        sendOdrDateCalendarOpenTracking();
    };

    const resetFilters = () => {
        setCurrentSearch('');
        setCurrentDate('');

        const newSearchParams = resetSearchParams(searchParams);
        if (countActiveFiltersClearable(wallConfig.filters, currentFilters)) {
            void onFilter(resetClearableActiveFilters(wallConfig.filters, currentFilters), newSearchParams);
        }

        const queryString = newSearchParams.toString();
        const url = queryString ? `${wallConfig.baseUrl}?${queryString}` : wallConfig.baseUrl;
        replaceWindowUrl(url);
        sendResetFilterTracking(false);
    };

    const updateProducts = async (generatedUrl?: string) => {
        let newSearchParams: URLSearchParams | undefined;
        let filters: ActiveFiltersType = currentFilters;

        if (generatedUrl !== undefined) {
            const [filterString, queryParams] = generatedUrl.split('?');

            newSearchParams = new URLSearchParams(queryParams);
            filters = getActiveFilters(
                wallConfig,
                (filterString ?? '').replace('filtre/', '').toLowerCase(),
                newSearchParams,
            );

            let url = `${wallConfig.baseUrl}/`;
            if (generatedUrl.startsWith('?')) {
                url = url.slice(0, -1);
            }
            url += generatedUrl;
            if (url.endsWith('/')) {
                url = url.slice(0, -1);
            }

            replaceWindowUrl(url);
            setSearchParams(newSearchParams);

            setHasToScroll((prev) => prev + 1);
        }

        wallService
            .getOdrProducts({
                filters,
                search: newSearchParams?.get('nom') ?? undefined,
            })
            .then((result) => {
                setProductsList(result.products);
                setDocumentTitle(generateTitle(title, undefined, { activeFilters: filters, wallConfig }));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const scrollToTop = () => {
        window.document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        sendOdrWallPageTracking();
    }, []);

    useEffect(() => {
        if (hasToScroll) {
            scrollToTop();
        }
    }, [hasToScroll]);

    return (
        <>
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <PageHeader
                    title={generateTitle(title, undefined, { wallConfig, activeFilters: currentFilters })}
                    subtitle="Retrouvez ici l'ensemble des offres de remboursement auxquelles vous pouvez souscrire"
                />
            </Section>
            <Section id='products' backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <View flexable justify={Justify.SPACE_BETWEEN} align={Align.CENTER} className='gap-medium flex-wrap'>
                    <View flexable align={Align.CENTER} fullwidth={false} className='gap-medium'>
                        <Title level={TitleLevels.FOUR} marginless className='no-wrap'>
                            Filtrez
                        </Title>
                        {(countActiveFiltersClearable(filters, currentFilters) > 0 || currentSearch || currentDate) && (
                            <Button
                                variant={ButtonVariant.GHOST}
                                onClick={resetFilters}
                                iconName={IconName.UNDO}
                                className='is-hidden-mobile'
                            >
                                &nbsp;Réinitialiser les filtres
                            </Button>
                        )}
                    </View>
                </View>
                <Spacer size={SpacerSize.FOUR} />
                <View className='sticky-padding is-sticky-top py-small has-background-grey-fade'>
                    <FlexBox align={Align.CENTER}>
                        <FlexItem size={12}>
                            {filters.length > 0 && (
                                <Filter
                                    onFilter={onFilter}
                                    availableFilters={filters}
                                    activeFilters={currentFilters}
                                    href={href}
                                    onChange={onFilterTracking}
                                    linkAs={LinkCustom}
                                    resetFilters={resetFilters}
                                    count={count}
                                />
                            )}
                        </FlexItem>
                    </FlexBox>
                </View>
                <Spacer size={SpacerSize.FOUR} />
                <FlexBox gap={4}>
                    <FlexItem size={{ mobile: 12, tablet: 6, desktop: 3 }}>
                        <Input
                            label='Rechercher'
                            type={InputType.SEARCH}
                            value={currentSearch}
                            onChange={(e) => onSearch(e.inputValue)}
                        />
                    </FlexItem>
                    <FlexItem size={{ mobile: 12, tablet: 6, desktop: 3 }}>
                        <Input
                            label="Date d'achat ou de souscription"
                            type={InputType.DATE}
                            value={currentDate}
                            onChange={(e) => onDateChange(e.inputValue)}
                            onIconClick={onDateClick}
                        />
                    </FlexItem>
                </FlexBox>
                <Spacer size={SpacerSize.EIGHT} />
                <Text
                    level={TextLevels.ONE}
                    aria-live='polite'
                    aria-atomic='true'
                    typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}
                    accessibilityLabel={`${count} ${wallConfig.productType.toLowerCase()}${count > 1 ? 's' : ''} disponible${count > 1 ? 's' : ''}`}
                >
                    {count} {wallConfig.productType}
                    {count > 1 ? 's' : ''}
                </Text>
                <ProductList
                    isLoading={isLoading}
                    products={isLoading ? odrSkeleton : filteredProducts.slice(0, currentPage * pageSize)}
                    imageAs={OdrImage}
                    onClick={(product) => loadOdrDetails(product.id)}
                    data-cy='product-list'
                    namePosition={NamePosition.BELOW}
                    priceLevel={PriceLevel.FOUR}
                    helpLevel={TextLevels.TWO}
                    boxProps={{
                        shadowless: false,
                        flat: false,
                        active: false,
                        background: (p) => {
                            return (p as OdrProduct).inStock ? TrilogyColor.BACKGROUND : TrilogyColor.DISABLED_FADE;
                        },
                    }}
                    imageSizes={{
                        width: 100,
                        height: 80,
                    }}
                />
                {count <= 0 && !isLoading && <EmptyProductList resetFilters={resetFilters} />}
                <Spacer size={SpacerSize.FIVE} />
                {filteredProducts.length >= currentPage * pageSize && (
                    <div className='has-text-centered'>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                            accessibilityLabel={`Afficher plus de produits, ${filteredProducts.length - currentPage * pageSize} restants`}
                        >
                            Afficher plus d&apos;offres ({filteredProducts.length - currentPage * pageSize})
                        </Button>
                    </div>
                )}
            </Section>
            <OdrDetailModal selectedOdr={selectedOdr} onClose={handleCloseModal} />
            <Section backgroundColor={TrilogyColor.BACKGROUND}>
                <Container>
                    <Breadcrumb wallConfig={wallConfig} activeFilters={currentFilters} />
                </Container>
            </Section>
        </>
    );
}
